import type { ButtonHTMLAttributes, ReactNode } from 'react'

/**
 * Round 36×36 chrome button for header actions: theme toggle, close, etc.
 *
 * Variants:
 *  - `default` — neutral surface, muted icon. Standard action.
 *  - `accent`  — filled with brand-primary-strong. Use when toggle is "on".
 */

export type IconButtonVariant = 'default' | 'accent'

interface IconButtonProps
  extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'className' | 'children'
  > {
  variant?: IconButtonVariant
  children: ReactNode
  'aria-label': string
  className?: string
}

const VARIANT_CLASSES: Record<IconButtonVariant, string> = {
  default:
    'bg-brand-surface text-brand-text-muted border border-brand-border hover:bg-brand-bg-soft',
  accent:
    'bg-brand-primary-strong text-brand-on-primary border border-brand-primary-strong',
}

export function IconButton({
  variant = 'default',
  children,
  className = '',
  ...rest
}: IconButtonProps) {
  return (
    <button
      className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors cursor-pointer ${VARIANT_CLASSES[variant]} ${className}`}
      {...rest}
    >
      <span className="w-4 h-4 flex items-center justify-center">
        {children}
      </span>
    </button>
  )
}
